import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { PREPIONEERS_TOTAL } from '../founders-config';
import { FounderSlot } from './founder-slot';

// Same look as FoundersGrid, but 500K cards can't live in the DOM at
// once. We render a window [start, end] and grow it by CHUNK when the
// sentinel under the grid scrolls into view.
const CHUNK = 120;
const GRID =
  'grid snap-y snap-mandatory grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

function useGrowingEnd(start: number): {
  end: number;
  sentinel: RefObject<HTMLDivElement | null>;
} {
  const [count, setCount] = useState(CHUNK);
  const sentinel = useRef<HTMLDivElement>(null);
  const end = Math.min(PREPIONEERS_TOTAL, start + count - 1);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || end >= PREPIONEERS_TOTAL) return;
    const io = new globalThis.IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setCount((c) => c + CHUNK);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [end]);
  return { end, sentinel };
}

export function scrollToPrepioneer(n: number): void {
  const el = document.getElementById(`prepioneer-${n}`);
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

// Remount with `key={start}` from the parent to reset the window after
// a jump. On mount we scroll the first card into view when start > 1.
export function PrepioneersGrid({ start }: { start: number }): ReactNode {
  const { end, sentinel } = useGrowingEnd(start);
  useEffect(() => {
    if (start > 1) scrollToPrepioneer(start);
  }, [start]);
  const slots = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  return (
    <>
      <div className={GRID}>
        {slots.map((n) => (
          <FounderSlot key={n} n={n} idPrefix="prepioneer" />
        ))}
      </div>
      <div ref={sentinel} aria-hidden className="h-px" />
    </>
  );
}
